// One controller owns each card's selection and add state. No product JSON is serialized.
export function variantPriceState({ price, compareAt, available }) {
  return { sale: Number(compareAt) > Number(price), soldOut: !available };
}

export function salePercent({ price, compareAt }) {
  const compare = Number(compareAt);
  return compare > Number(price) && compare > 0 ? Math.round(((compare - Number(price)) * 100) / compare) : 0;
}

export function resolveCardAction({ mode, purchasableCount, optionCount, explicitSelection, available, completeVariantList = true, productAvailable = true, requiresSellingPlan = false }) {
  if (mode === 'OFF') return 'off';
  if (!productAvailable || (completeVariantList && purchasableCount === 0)) return 'sold_out';
  if (requiresSellingPlan) return 'choose';
  if (!available && explicitSelection) return 'sold_out';
  if (!available) return 'choose';
  if (completeVariantList && purchasableCount === 1) return 'add';
  if (mode === 'SMART' && optionCount === 1 && explicitSelection) return 'add';
  return 'choose';
}

export function productVariantUrl(path, variantId, base) {
  const url = new URL(path, base);
  if (variantId) url.searchParams.set('variant', variantId);
  else url.searchParams.delete('variant');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function cartAjaxUrl(action) {
  const url = new URL(action);
  url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
  return url.href;
}

// asset_url and relative imports can create separate module instances. Keep
// ownership on the card so both URLs share one controller and submit handler.
const controllerKey = Symbol.for('nawiya.product-card.controller');

class ProductCard {
  constructor(root) {
    this.root = root;
    this.abort = new AbortController();
    this.busy = false;
    this.lastSuccessAt = 0;
    this.explicitSelection = false;
    this.variant = {
      id: root.dataset.cardVariantId || '',
      available: root.dataset.cardAvailable === 'true',
      price: Number(root.dataset.cardPrice || 0),
      compareAt: Number(root.dataset.cardCompare || 0),
      preorder: root.dataset.cardPreorder === 'true'
    };
    this.formWrap = root.querySelector('[data-card-add-form]');
    this.form = this.formWrap?.querySelector('form');
    this.button = root.querySelector('[data-card-add-button]');
    this.choose = root.querySelector('[data-card-choose-link]');
    this.feedback = root.querySelector('[data-card-feedback]');
    this.media = root.querySelector('[data-card-media]');
    this.media?.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' && matchMedia('(hover: hover)').matches) this.loadSecondary();
    }, { signal: this.abort.signal });
    this.media?.addEventListener('focusin', (event) => {
      if (event.target.matches(':focus-visible')) this.loadSecondary();
    }, { signal: this.abort.signal });
    root.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-card-swatch]');
      if (swatch && root.contains(swatch)) this.select(swatch);
    }, { signal: this.abort.signal });
    this.form?.addEventListener('submit', (event) => this.submit(event), { signal: this.abort.signal });
    this.renderAction();
  }

  loadSecondary() {
    const template = this.root.querySelector('[data-card-secondary-template]');
    const slot = this.root.querySelector('[data-card-secondary-slot]');
    if (!template || !slot || slot.firstElementChild) return;
    const image = template.content.firstElementChild?.cloneNode(true);
    if (!image) return;
    image.loading = 'eager';
    slot.append(image);
    this.root.dataset.cardSecondaryLoaded = 'true';
  }

  select(button) {
    if (this.busy) return;
    this.explicitSelection = true;
    this.root.dataset.cardVariantSelected = 'true';
    for (const item of this.root.querySelectorAll('[data-card-swatch]')) {
      item.setAttribute('aria-pressed', String(item === button));
    }
    const data = button.dataset;
    this.variant = {
      id: data.variantId || '',
      available: data.available === 'true',
      price: Number(data.price || 0),
      compareAt: Number(data.compare || 0),
      preorder: data.preorder === 'true'
    };
    this.root.dataset.cardVariantId = this.variant.id;
    this.root.dataset.cardAvailable = String(this.variant.available);
    this.root.querySelector('[data-card-price]').textContent = data.priceText || '';
    const compare = this.root.querySelector('[data-card-compare]');
    const state = variantPriceState(this.variant);
    // Store styles colour the current price while the variant is on sale (style-shared.css "Sale").
    this.root.querySelector('[data-card-price-wrap]')?.toggleAttribute('data-on-sale', state.sale);
    if (compare) {
      compare.textContent = state.sale ? data.compareText || '' : '';
      compare.hidden = !state.sale;
    }
    const sale = this.root.querySelector('[data-card-sale]');
    const soldOut = this.root.querySelector('[data-card-sold-out]');
    if (sale) {
      sale.hidden = !state.sale || state.soldOut;
      if (state.sale && sale.dataset.badgeTemplate) sale.textContent = sale.dataset.badgeTemplate.replace('PERCENT_TOKEN', String(salePercent(this.variant)));
    }
    const preorder = this.root.querySelector('[data-card-preorder]');
    if (preorder) preorder.hidden = !this.variant.preorder || state.soldOut;
    this.root.dataset.cardPreorder = String(Boolean(this.variant.preorder));
    if (soldOut) soldOut.hidden = !state.soldOut;
    const image = this.root.querySelector('.product-card__main-image');
    if (image && data.imageSrc) {
      image.src = data.imageSrc;
      image.srcset = data.imageSrcset || '';
      image.alt = data.imageAlt || '';
      if (data.imageRatio) this.media?.style.setProperty('--card-natural-ratio', data.imageRatio);
    }
    const base = this.root.dataset.cardBaseUrl;
    const url = productVariantUrl(base, this.variant.id, location.origin);
    for (const link of this.root.querySelectorAll('[data-card-product-link]')) link.href = url;
    if (this.choose) this.choose.href = url;
    const input = this.form?.querySelector('[name="id"]');
    if (input) input.value = this.variant.id;
    if (this.feedback) this.feedback.textContent = '';
    this.root.dataset.cardStatus = state.soldOut ? 'sold_out' : 'idle';
    this.renderAction();
  }

  renderAction() {
    const action = resolveCardAction({
      mode: this.root.dataset.cardQuickAddMode,
      purchasableCount: Number(this.root.dataset.cardPurchasableCount || 0),
      optionCount: Number(this.root.dataset.cardOptionCount || 0),
      explicitSelection: this.explicitSelection,
      available: this.variant.available,
      completeVariantList: this.root.dataset.cardCompleteVariants !== 'false',
      productAvailable: this.root.dataset.cardProductAvailable !== 'false',
      requiresSellingPlan: this.root.dataset.cardRequiresSellingPlan === 'true'
    });
    if (action === 'sold_out') this.root.dataset.cardStatus = 'sold_out';
    else if (!this.root.dataset.cardStatus || this.root.dataset.cardStatus === 'sold_out') this.root.dataset.cardStatus = 'idle';
    if (this.formWrap) this.formWrap.hidden = action === 'choose' || action === 'off';
    if (this.choose) this.choose.hidden = action !== 'choose';
    if (this.button) {
      this.button.disabled = action !== 'add' || this.busy;
      this.button.textContent = action === 'sold_out' ? this.root.dataset.cardSoldOutLabel : (this.variant.preorder && this.root.dataset.cardPreorderLabel) || this.root.dataset.cardAddLabel;
    }
    return action;
  }

  async submit(event) {
    // Respect an existing submit owner, including an older cached module.
    if (event.defaultPrevented) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy || Date.now() - this.lastSuccessAt < 800 || !this.form || this.renderAction() !== 'add') return;
    const input = this.form.querySelector('[name="id"]');
    if (!input || !this.variant.id || input.value !== this.variant.id || !this.variant.available) return;
    this.busy = true;
    this.root.dataset.cardStatus = 'loading';
    this.button.disabled = true;
    this.button.textContent = this.root.dataset.cardLoadingLabel;
    try {
      const response = await fetch(cartAjaxUrl(this.form.action), {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abort.signal
      });
      if (!response.ok) throw new Error('Add request failed');
      await response.json();
      this.lastSuccessAt = Date.now();
      this.root.dataset.cardStatus = 'success';
      if (this.feedback) this.feedback.textContent = this.root.dataset.cardSuccessLabel;
      this.root.ownerDocument.dispatchEvent(new CustomEvent('nawiya:cart:changed', {
        detail: { source: 'product-card', variantId: this.variant.id, quantity: 1 }
      }));
    } catch (error) {
      if (this.abort.signal.aborted) return;
      this.root.dataset.cardStatus = 'error';
      if (this.feedback) this.feedback.textContent = this.root.dataset.cardErrorLabel;
    } finally {
      this.busy = false;
      if (!this.abort.signal.aborted) this.renderAction();
    }
  }

  destroy() {
    this.abort.abort();
    delete this.root[controllerKey];
  }
}

function cardsWithin(root) {
  return [...(root.matches?.('[data-product-card]') ? [root] : []), ...root.querySelectorAll('[data-product-card]')];
}

export function mountProductCards(root = document) {
  let added = false;
  for (const card of cardsWithin(root)) if (!card[controllerKey]) { card[controllerKey] = new ProductCard(card); added = true; }
  if (added) document.dispatchEvent(new CustomEvent('nawiya:cards:mounted', { detail: { scope: root } }));
}

export function unmountProductCards(root) {
  for (const card of cardsWithin(root)) card[controllerKey]?.destroy();
}

if (typeof document !== 'undefined') {
  mountProductCards();
  document.addEventListener('shopify:section:load', (event) => mountProductCards(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmountProductCards(event.target));
}
